import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useClickOutside from '../../../../hooks/useClickOutside';
import { Filter, X, Star } from 'lucide-react';

// Interface de controle de filtros para Leads Salvos
export interface LeadsControls {
  rating: number; // 0 to 5
  whatsappStatus: 'all' | 'validated' | 'pending' | 'invalid';
  sortBy: 'date_desc' | 'date_asc' | 'name_asc' | 'name_desc' | 'rating_desc' | 'rating_asc';
}

interface LeadsFilterDropdownProps {
  controls: LeadsControls;
  setControls: React.Dispatch<React.SetStateAction<LeadsControls>>;
  isOpen: boolean;
  onClose: () => void;
  onClearFilters: () => void;
}

const LeadsFilterDropdown: React.FC<LeadsFilterDropdownProps> = ({
  controls,
  setControls,
  isOpen,
  onClose,
  onClearFilters,
}) => {
  const { t } = useTranslation();
  const dropdownRef = useClickOutside<HTMLDivElement>(() => {
    if (isOpen) onClose();
  });

  const [localControls, setLocalControls] = useState(controls);

  useEffect(() => {
    if (isOpen) {
      setLocalControls(controls);
    }
  }, [isOpen, controls]);

  const handleApply = () => {
    setControls(localControls);
    onClose();
  };

  const handleClearLocal = () => {
    const defaultControls: LeadsControls = {
      rating: 0,
      whatsappStatus: 'all',
      sortBy: 'date_desc',
    };
    setLocalControls(defaultControls);
    onClearFilters(); // Chama a função de limpar filtros no LeadsSalvos.tsx
  };

  const whatsappOptions = [
    { label: t('dashboard.filter_whatsapp_default'), value: 'all' },
    { label: t('dashboard.status_validated'), value: 'validated' },
    { label: t('dashboard.status_pending'), value: 'pending' },
    { label: t('dashboard.status_invalid'), value: 'invalid' },
  ];

  const sortOptions = [
    { label: t('leads.sort_date_desc'), value: 'date_desc' },
    { label: t('leads.sort_date_asc'), value: 'date_asc' },
    { label: t('dashboard.filter_sort_name_asc'), value: 'name_asc' },
    { label: t('dashboard.filter_sort_name_desc'), value: 'name_desc' },
    { label: t('dashboard.filter_sort_rating_desc'), value: 'rating_desc' },
    { label: t('dashboard.filter_sort_rating_asc'), value: 'rating_asc' },
  ];

  if (!isOpen) return null;

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        className="relative w-full max-w-lg rounded-xl bg-white dark:bg-background-dark-elevated shadow-2xl border border-gray-200 dark:border-primary-darkMode p-6 flex flex-col gap-6"
        initial={{ y: 50, opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: 50, opacity: 0, scale: 0.95 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        onClick={(e) => e.stopPropagation()}
        ref={dropdownRef}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-primary-darkMode pb-4">
          <h2 className="text-xl font-bold text-[#181411] dark:text-white flex items-center gap-2">
            <Filter className="size-6 text-primary" /> {t('leads.action_filter')}
          </h2>
          <motion.button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10"
            whileHover={{ rotate: 90 }}
            whileTap={{ scale: 0.9 }}
          >
            <X className="size-5 text-gray-500 dark:text-gray-400" />
          </motion.button>
        </div>

        {/* Body - Filters */}
        <div className="space-y-6">
          {/* Status WhatsApp */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">{t('dashboard.table_header_whatsapp')}</label>
            <div className="flex flex-wrap gap-2">
              {whatsappOptions.map((option) => (
                <motion.button
                  key={option.value}
                  type="button"
                  onClick={() => setLocalControls(prev => ({ ...prev, whatsappStatus: option.value as LeadsControls['whatsappStatus'] }))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${localControls.whatsappStatus === option.value
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-gray-100 dark:bg-background-dark-elevated2 text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-background-dark-elevated3'
                    }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {option.label}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Rating */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">{t('dashboard.table_header_rating')}</label>
            <div className="flex items-center gap-2">
              {[0, 1, 2, 3, 4, 5].map((rating) => (
                <motion.button
                  key={rating}
                  type="button"
                  onClick={() => setLocalControls(prev => ({ ...prev, rating }))}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${localControls.rating === rating
                      ? 'bg-primary text-white shadow-md'
                      : 'bg-gray-100 dark:bg-background-dark-elevated2 text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-background-dark-elevated3'
                    }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {rating > 0 && (
                    <Star className={`size-4 ${localControls.rating >= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
                  )}
                  {rating === 0 ? t('dashboard.filter_clear') : rating}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Ordenação */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-white mb-2">{t('leads.sort_by')}</label>
            <div className="relative">
              <select
                value={localControls.sortBy}
                onChange={(e) => setLocalControls(prev => ({ ...prev, sortBy: e.target.value as LeadsControls['sortBy'] }))}
                className="w-full appearance-none rounded-lg border-gray-300 dark:border-primary-darkMode bg-white dark:bg-background-dark-elevated2 py-2.5 pl-4 pr-10 text-sm text-[#181411] dark:text-white focus:border-primary focus:ring-primary"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <span className="material-symbols-outlined pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">expand_more</span>
            </div>
          </div>
        </div>

        {/* Footer / Actions */}
        <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700/50">
          <motion.button
            type="button"
            onClick={handleClearLocal}
            className="flex h-10 w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 text-sm font-bold px-4 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('dashboard.filter_clear')}
          </motion.button>
          <motion.button
            type="button"
            onClick={handleApply}
            className="flex h-10 w-auto cursor-pointer items-center justify-center overflow-hidden rounded-lg bg-primary text-white text-sm font-bold px-4 hover:opacity-90 transition-opacity"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {t('Aplicar Filtros')}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default LeadsFilterDropdown;